import { useState, useCallback, useRef } from 'react';
import socketService from '../services/socketService';
import { convertBackendMessages } from '../utils/messageUtils';

/**
 * 消息管理 hook
 * 负责：消息状态、流式消息处理、消息同步
 */
export function useMessages(selectedConversation, loadConversationMessages) {
  const [messages, setMessages] = useState([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const currentConvIdRef = useRef(null);

  // 同步当前对话ID到ref
  if (selectedConversation?._id) {
    currentConvIdRef.current = String(selectedConversation._id);
  } else {
    currentConvIdRef.current = null;
  }

  // 加载历史消息
  const loadMessages = useCallback(async (convId) => {
    const rawMessages = await loadConversationMessages(convId);
    if (!rawMessages) return;
    const converted = convertBackendMessages(rawMessages);
    setMessages(converted.map(msg => ({ ...msg, isSynced: false })));
  }, [loadConversationMessages]);

  // 发送消息
  const sendMessage = useCallback((content) => {
    if (!selectedConversation || !content.trim() || isStreaming) return;

    const convId = String(selectedConversation._id);
    setIsStreaming(true);

    const userMessage = {
      content: content.trim(),
      isUser: true,
      timestamp: new Date().toISOString(),
      type: 'user',
    };

    setMessages(prev => [...prev, userMessage]);
    socketService.emit('send_stream_message', {
      content: content.trim(),
      conversationId: convId,
    });
  }, [selectedConversation, isStreaming]);

  // 处理来自 socket 的消息同步
  const handleMessageSync = useCallback((data) => {
    const currentConvId = currentConvIdRef.current;

    if (!currentConvId || currentConvId !== data.conversationId) return;

    switch (data.type) {
      case 'stream_user_message': {
        const currentSocketId = socketService.getSocketId();
        setMessages(prev => {
          if (data.from && data.from === currentSocketId) {
            setIsStreaming(true);
            return [...prev, {
              content: '',
              isUser: false,
              timestamp: data.timestamp || new Date().toISOString(),
              type: 'assistant',
              isSynced: false,
              _id: `temp-${Date.now()}`,
            }];
          }
          return [...prev, {
            content: data.content,
            isUser: true,
            timestamp: data.timestamp || new Date().toISOString(),
            type: 'user',
            isSynced: true,
          }, {
            content: '',
            isUser: false,
            timestamp: data.timestamp || new Date().toISOString(),
            type: 'assistant',
            isSynced: false,
          }];
        });
        break;
      }
      case 'stream_ai_message':
        setIsStreaming(true);
        setMessages(prev => {
          const updated = [...prev];
          const last = updated[updated.length - 1];
          updated[updated.length - 1] = {
            timestamp: last?.timestamp || Date.now(),
            isUser: false,
            type: 'assistant',
            content: (last?.content || '') + data.content,
            isSynced: false,
          };
          return updated;
        });
        break;
      case 'stream_ai_message_end':
        setIsStreaming(false);
        setMessages(prev => {
          const updated = [...prev];
          const last = updated[updated.length - 1];
          if (last && !last.isUser && last.type === 'assistant' && data.message) {
            updated[updated.length - 1] = {
              ...data.message,
              isUser: false,
              type: 'assistant',
              isSynced: false,
              id: data.message._id || data.message.id,
            };
          }
          return updated;
        });
        break;
      default:
        break;
    }
  }, []);

  const clearMessages = useCallback(() => setMessages([]), []);

  return {
    messages,
    isStreaming,
    setIsStreaming,
    setMessages,
    clearMessages,
    loadMessages,
    sendMessage,
    handleMessageSync,
  };
}